import { useState, useEffect } from 'react';
import { useTheme } from '../../../context/theme';
import { useToast } from '../../../context/toast';
import { api } from '../../../utils/api';
import { renderIcon } from '../../../utils/icons';
import { Btn, Inp, Card, EmptyState } from '../../../components/ui';
import { SkeletonList } from '../../../components/ui/Skeletons';

export default function VendorsTab({ siteId, site, user }) {
  const { theme, isDark } = useTheme();
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', address: '', ein_ssn: '', vendor_type: 'Other', sage_vendor_id: '', notes: '' });
  const isViewer = user.role === 'viewer';
  const isAdmin = user.role === 'admin';
  const vendorTypes = ['Environmental', 'Geotech', 'Surveyor', 'Title Co', 'Attorney', 'Civil Engineer', 'Land Planner', 'Traffic Engineer', 'Utility Consultant', 'Government', 'Other'];

  const load = () => api.get('/api/sites/' + siteId + '/vendors').then(d => { setVendors(d.vendors || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, [siteId]);

  const save = async () => {
    try {
      if (!form.company_name) return toast('Company name required', 'error');
      if (editing) { await api.put('/api/vendors/' + editing, form); toast('Vendor updated'); }
      else { await api.post('/api/sites/' + siteId + '/vendors', form); toast('Vendor created'); }
      setShowForm(false); setEditing(null);
      setForm({ company_name: '', contact_name: '', email: '', phone: '', address: '', ein_ssn: '', vendor_type: 'Other', sage_vendor_id: '', notes: '' });
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!confirm('Delete this vendor?')) return;
    try { await api.delete('/api/vendors/' + id); toast('Deleted'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const uploadW9 = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try { await fetch('/api/vendors/' + id + '/w9', { method: 'POST', headers: { Authorization: 'Bearer ' + api.token }, body: fd }); toast('W-9 uploaded'); load(); }
    catch (e) { toast('Upload failed', 'error'); }
  };

  const uploadCoi = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try { await fetch('/api/vendors/' + id + '/coi', { method: 'POST', headers: { Authorization: 'Bearer ' + api.token }, body: fd }); toast('COI uploaded'); load(); }
    catch (e) { toast('Upload failed', 'error'); }
  };

  const verifyW9 = async (id) => {
    try { await api.put('/api/vendors/' + id + '/w9-status', { status: 'verified' }); toast('W-9 verified'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const w9Badge = (s) => s === 'verified'
    ? <span style={{ background: '#dcfce7', color: '#4ADE80', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>✅ Verified</span>
    : s === 'uploaded'
      ? <span style={{ background: '#fef3c7', color: '#F59E0B', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>⏳ Uploaded</span>
      : <span style={{ background: '#fecaca', color: '#EF4444', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>❌ Missing</span>;

  if (loading) return <Card><SkeletonList count={3} /></Card>;

  const selStyle = { padding: '8px 12px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 8, color: theme.text, fontSize: 13 };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>
          Vendors<span style={{ color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>({vendors.length})</span>
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href="/accounting?tab=vendors" onClick={e => { e.preventDefault(); window.history.pushState({ view: 'accounting' }, '', '/accounting?tab=vendors'); window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'accounting' } })); }} style={{ fontSize: 12, color: theme.accent, textDecoration: 'none' }}>Manage in Accounting →</a>
          {!isViewer && (
            <Btn sm primary onClick={() => { setEditing(null); setForm({ company_name: '', contact_name: '', email: '', phone: '', address: '', ein_ssn: '', vendor_type: 'Other', sage_vendor_id: '', notes: '' }); setShowForm(!showForm); }}>
              {showForm ? 'Cancel' : '+ Add Vendor'}
            </Btn>
          )}
          <a href={'/api/sites/' + siteId + '/vendors/export'} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid ' + theme.border, borderRadius: 8, color: theme.textSecondary, fontSize: 12, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            {renderIcon('download', 12)} CSV
          </a>
        </div>
      </div>

      {showForm && (
        <div style={{ background: isDark ? '#1e1e24' : '#f8f9fa', border: '1px solid ' + theme.border, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Inp value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="Company Name *" />
            <select value={form.vendor_type} onChange={e => setForm({ ...form, vendor_type: e.target.value })} style={selStyle}>
              {vendorTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Inp value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Contact Name" />
            <Inp value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" />
            <Inp value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" />
            <Inp value={form.ein_ssn} onChange={e => setForm({ ...form, ein_ssn: e.target.value })} placeholder="EIN / SSN" />
            <Inp value={form.sage_vendor_id} onChange={e => setForm({ ...form, sage_vendor_id: e.target.value })} placeholder="Sage Vendor ID" />
          </div>
          <Inp value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" style={{ marginTop: 10, width: '100%' }} />
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes" style={{ width: '100%', padding: '8px 12px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 8, color: theme.text, fontSize: 13, marginTop: 10, minHeight: 40, resize: 'vertical', boxSizing: 'border-box' }} />
          <Btn sm primary onClick={save} style={{ marginTop: 10 }}>{editing ? 'Update Vendor' : 'Create Vendor'}</Btn>
        </div>
      )}

      {vendors.length === 0 && !showForm && <EmptyState icon="users" title="No vendors yet" description="Add vendors to track W-9s, COIs, and manage purchase orders" action={!isViewer ? '+ Add Vendor' : undefined} onAction={!isViewer ? () => { setEditing(null); setForm({ company_name: '', contact_name: '', email: '', phone: '', address: '', ein_ssn: '', vendor_type: 'Other', sage_vendor_id: '', notes: '' }); setShowForm(true); } : undefined} />}

      {vendors.length > 0 && (
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid ' + theme.border }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 80px', padding: '8px 12px', background: isDark ? '#2a2a32' : '#f1f5f9', fontSize: 11, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, gap: 8 }}>
            <span>Vendor</span><span>Type</span><span>W-9</span><span>COI</span><span>Total POs</span><span>Total Paid</span><span></span>
          </div>
          {vendors.map((v, i) => (
            <div key={v.id} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 80px', padding: '10px 12px', borderTop: '1px solid ' + theme.border, fontSize: 13, alignItems: 'center', gap: 8, cursor: 'default', background: i % 2 === 0 ? 'transparent' : (isDark ? '#ffffff05' : 'rgba(255,255,255,0.03)') }}>
              <div>
                <div style={{ fontWeight: 600, color: theme.text }}>{v.company_name}</div>
                {v.contact_name && <div style={{ fontSize: 11, color: theme.textSecondary }}>{v.contact_name}</div>}
              </div>
              <span style={{ fontSize: 11, color: theme.textSecondary }}>{v.vendor_type}</span>
              <div>
                {w9Badge(v.w9_status)}
                {v.w9_status === 'uploaded' && isAdmin && <button onClick={() => verifyW9(v.id)} style={{ marginLeft: 4, background: 'none', border: 'none', color: '#4ADE80', cursor: 'pointer', fontSize: 10, textDecoration: 'underline' }}>Verify</button>}
                {v.w9_status === 'missing' && !isViewer && (
                  <label style={{ display: 'block', marginTop: 4, fontSize: 10, color: '#4ADE80', cursor: 'pointer' }}>
                    <input type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) uploadW9(v.id, e.target.files[0]); }} />Upload W-9
                  </label>
                )}
              </div>
              <div>
                {v.coi_blob_url
                  ? <span style={{ fontSize: 11, color: v.coi_expiration_date && new Date(v.coi_expiration_date) < new Date() ? '#EF4444' : '#4ADE80' }}>✅ {v.coi_expiration_date ? 'Exp ' + new Date(v.coi_expiration_date).toLocaleDateString() : 'On file'}</span>
                  : <div><span style={{ fontSize: 11, color: theme.textSecondary }}>None</span>{!isViewer && <label style={{ display: 'block', fontSize: 10, color: '#4ADE80', cursor: 'pointer' }}><input type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) uploadCoi(v.id, e.target.files[0]); }} />Upload</label>}</div>
                }
              </div>
              <span style={{ fontWeight: 600, color: theme.text }}>{v.total_pos || 0}</span>
              <span style={{ fontWeight: 600, color: '#4ADE80' }}>${(Number(v.total_paid) || 0).toLocaleString()}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {!isViewer && <button onClick={() => { setEditing(v.id); setForm({ company_name: v.company_name, contact_name: v.contact_name || '', email: v.email || '', phone: v.phone || '', address: v.address || '', ein_ssn: v.ein_ssn || '', vendor_type: v.vendor_type || 'Other', sage_vendor_id: v.sage_vendor_id || '', notes: v.notes || '' }); setShowForm(true); }} style={{ background: 'none', border: 'none', color: '#4ADE80', cursor: 'pointer', fontSize: 12 }}>Edit</button>}
                {isAdmin && <button onClick={() => del(v.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12 }}>×</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
